import { useState, useRef } from 'react';
import { Trans } from '@lingui/macro';
import styled from '@emotion/styled';
import { Text } from 'components/text/Text';
import { TextInput, TextInputProps } from 'components/inputs/TextInput';

type FractionProps = {
  from?: string | '';
  to?: string | '';
  onChange: (from?: string | '', to?: string | '') => void;
  onBlur: () => void;
  onKeyDown?: TextInputProps['onKeyDown'];
  fromPlaceholder?: string;
  toPlaceholder?: string;
  'data-testid'?: string;
};

/**
 * Validates if the input is a valid number, decimal, or fraction format
 * Only allows specific fraction denominators: 1, 2, 4, 8, 16, 32
 * Allows partial input while typing
 * Examples: "5", "1.5", "1/2", "3/8", "1 1/2", "2 3/16"
 */
const isValidFractionInput = (value: string): boolean => {
  if (!value || value.trim() === '') {
    return true; // Allow empty input
  }

  const trimmed = value.trim();
  const allowedDenominators = [1, 2, 4, 8, 16, 32];

  // Allow partial decimal input: "5", "5.", "5.2"
  if (/^(\d+\.?\d*)$/.test(trimmed) || /^(\.\d*)$/.test(trimmed)) {
    return true;
  }

  // Allow partial fraction input: "5/", "5/2"
  const partialFractionPattern = /^(\d+)\/(\d*)$/;
  const partialFractionMatch = trimmed.match(partialFractionPattern);
  if (partialFractionMatch) {
    const denomStr = partialFractionMatch[2];
    // If denominator is empty (just typed "/"), allow it
    if (denomStr === '') {
      return true;
    }
    // If denominator is complete, check if it's allowed
    const denominator = parseInt(denomStr, 10);
    return allowedDenominators.includes(denominator);
  }

  // Allow partial mixed number input: "5 ", "5 3", "5 3/", "5 3/8"
  const partialMixedPattern = /^(\d+)\s+(\d*)$/;
  const partialMixedMatch = trimmed.match(partialMixedPattern);
  if (partialMixedMatch) {
    return true; // Allow "5 " or "5 3"
  }

  const partialMixedFractionPattern = /^(\d+)\s+(\d+)\/(\d*)$/;
  const partialMixedFractionMatch = trimmed.match(partialMixedFractionPattern);
  if (partialMixedFractionMatch) {
    const denomStr = partialMixedFractionMatch[3];
    // If denominator is empty (just typed "/"), allow it
    if (denomStr === '') {
      return true;
    }
    // If denominator is complete, check if it's allowed
    const denominator = parseInt(denomStr, 10);
    return allowedDenominators.includes(denominator);
  }

  return false;
};

/**
 * Converts a fraction string to a decimal number for comparison.
 * Returns null if the value is empty, partial, or unparseable.
 * Handles: integers ("5"), decimals ("1.5"), fractions ("3/8"), mixed numbers ("1 1/2")
 */
const parseFractionToDecimal = (value: string | undefined): number | null => {
  if (!value || value.trim() === '') return null;

  const trimmed = value.trim();

  // Plain integer or decimal: "5", "1.5"
  if (/^(\d+\.?\d*|\.\d+)$/.test(trimmed)) {
    const parsed = parseFloat(trimmed);
    return isNaN(parsed) ? null : parsed;
  }

  // Simple fraction: "3/8"
  const fractionMatch = trimmed.match(/^(\d+)\/(\d+)$/);
  if (fractionMatch) {
    const numerator = parseInt(fractionMatch[1], 10);
    const denominator = parseInt(fractionMatch[2], 10);
    return denominator !== 0 ? numerator / denominator : null;
  }

  // Mixed number: "1 1/2"
  const mixedMatch = trimmed.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixedMatch) {
    const whole = parseInt(mixedMatch[1], 10);
    const numerator = parseInt(mixedMatch[2], 10);
    const denominator = parseInt(mixedMatch[3], 10);
    return denominator !== 0 ? whole + numerator / denominator : null;
  }

  return null;
};

export const Fraction = ({
  from,
  to,
  onChange,
  onBlur,
  onKeyDown,
  toPlaceholder = 'e.g. 1 1/2',
  fromPlaceholder = 'e.g. 1/2',
  'data-testid': dataTestId,
}: FractionProps) => {
  const [rangeError, setRangeError] = useState(false);
  const pendingFrom = useRef(from);
  const pendingTo = useRef(to);

  const checkRangeError = (nextFrom: string | undefined, nextTo: string | undefined) => {
    const parsedFrom = parseFractionToDecimal(nextFrom);
    const parsedTo = parseFractionToDecimal(nextTo);
    const isInvalid = parsedFrom != null && parsedTo != null && parsedFrom > parsedTo;
    setRangeError(isInvalid);
    return !isInvalid;
  };

  return (
    <Container>
      <FromTo>
        <TextInput
          placeholder={fromPlaceholder}
          variant="outlined"
          autoFocus
          value={String(from ?? '')}
          onChange={e => {
            const value = e.target.value;
            if (isValidFractionInput(value)) {
              pendingFrom.current = value || '';
              onChange(value || '', to);
            }
          }}
          onBlur={() => {
            if (checkRangeError(pendingFrom.current, pendingTo.current)) onBlur();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              if (checkRangeError(pendingFrom.current, pendingTo.current)) {
                onKeyDown?.(e);
              }
            }
          }}
          data-testid={`${dataTestId}_from`}
        />
        <span data-testid={`${dataTestId}_divider_to`}>
          <Trans>to</Trans>
        </span>
        <TextInput
          placeholder={toPlaceholder}
          variant="outlined"
          value={String(to ?? '')}
          onChange={e => {
            const value = e.target.value;
            if (isValidFractionInput(value)) {
              pendingTo.current = value || '';
              onChange(from, value || '');
            }
          }}
          onBlur={() => {
            if (checkRangeError(pendingFrom.current, pendingTo.current)) onBlur();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              if (checkRangeError(pendingFrom.current, pendingTo.current)) {
                onKeyDown?.(e);
              }
            }
          }}
          data-testid={`${dataTestId}_to`}
        />
      </FromTo>
      {rangeError && (
        <Text color="error" variant="bodySm">
          <Trans>Start value must be lower than end value</Trans>
        </Text>
      )}
    </Container>
  );
};

const FromTo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  > div {
    min-width: 186px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  > span {
    margin-left: ${p => p.theme.s.xxxs};
    margin-right: ${p => p.theme.s.xxxs};
  }
`;
